"use client";

import { useState, useMemo, useCallback } from "react";
import { useRouter } from "next/navigation";
import { formatCompactNumber } from "@/lib/format";
import type { NicheRanking } from "@/lib/types";

type Props = {
  niches: NicheRanking[];
  medianDemand: number;
  medianStartups: number;
  medianRevPerVisitor: number;
};

type SortKey = "gapScore" | "marketDemand" | "avgRevenuePerVisitor" | "opportunityScore" | "startupCount";
type SortDir = "asc" | "desc";

const GAP_SCORE_HIGH = 4000;
const GAP_SCORE_MID = 2000;

function getGapScoreVariant(score: number | null): "high" | "mid" | "low" | "none" {
  if (score == null || !Number.isFinite(score)) return "none";
  if (score >= GAP_SCORE_HIGH) return "high";
  if (score >= GAP_SCORE_MID) return "mid";
  return "low";
}

export function GapFinder({
  niches,
  medianDemand,
  medianStartups,
  medianRevPerVisitor,
}: Props) {
  const router = useRouter();
  const [minDemand, setMinDemand] = useState(medianDemand);
  const [maxStartups, setMaxStartups] = useState(medianStartups);
  const [minRevPerVisitor, setMinRevPerVisitor] = useState(medianRevPerVisitor);
  const [sortKey, setSortKey] = useState<SortKey>("gapScore");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const maxDemand = useMemo(
    () => Math.max(...niches.map((n) => n.marketDemand ?? 0), 1),
    [niches]
  );
  const maxStartupsInData = useMemo(
    () => Math.max(...niches.map((n) => n.startupCount), 1),
    [niches]
  );
  const maxRevPerVisitor = useMemo(() => {
    const values = niches
      .map((n) => n.avgRevenuePerVisitor ?? 0)
      .filter((v) => Number.isFinite(v) && v > 0);
    return values.length > 0 ? Math.max(...values) : 1;
  }, [niches]);

  const resetFilters = useCallback(() => {
    setMinDemand(medianDemand);
    setMaxStartups(medianStartups);
    setMinRevPerVisitor(medianRevPerVisitor);
  }, [medianDemand, medianStartups, medianRevPerVisitor]);

  const presets = useMemo(
    () => ({
      goldmine: () => {
        setMinDemand(medianDemand);
        setMaxStartups(medianStartups);
        setMinRevPerVisitor(medianRevPerVisitor);
      },
      lowCompetition: () => {
        setMinDemand(0);
        setMaxStartups(Math.max(1, Math.floor(medianStartups * 0.5)));
        setMinRevPerVisitor(0);
      },
      highMonetization: () => {
        const revValues = niches
          .map((n) => n.avgRevenuePerVisitor ?? 0)
          .filter((v) => Number.isFinite(v) && v > 0)
          .sort((a, b) => b - a);
        const p75 = revValues[Math.floor(revValues.length * 0.25)] ?? medianRevPerVisitor;
        setMinDemand(0);
        setMaxStartups(maxStartupsInData);
        setMinRevPerVisitor(Math.max(p75, medianRevPerVisitor));
      },
      highDemand: () => {
        const demandValues = niches
          .map((n) => n.marketDemand ?? 0)
          .filter((v) => Number.isFinite(v))
          .sort((a, b) => b - a);
        const p75 = demandValues[Math.floor(demandValues.length * 0.25)] ?? medianDemand;
        setMinDemand(Math.max(p75, medianDemand));
        setMaxStartups(maxStartupsInData);
        setMinRevPerVisitor(0);
      },
    }),
    [
      niches,
      medianDemand,
      medianStartups,
      medianRevPerVisitor,
      maxStartupsInData,
    ]
  );

  const filtered = useMemo(() => {
    let result = niches
      .filter((n) => (n.marketDemand ?? 0) >= minDemand)
      .filter((n) => n.startupCount <= maxStartups)
      .filter((n) => (n.avgRevenuePerVisitor ?? 0) >= minRevPerVisitor);

    const mult = sortDir === "asc" ? 1 : -1;
    result = [...result].sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return mult * (bVal - aVal);
      }
      return 0;
    });
    return result;
  }, [niches, minDemand, maxStartups, minRevPerVisitor, sortKey, sortDir]);

  const topNiche = filtered[0];
  const hasFiltersChanged =
    minDemand !== medianDemand ||
    maxStartups !== medianStartups ||
    minRevPerVisitor !== medianRevPerVisitor;

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortHeader = ({
    label,
    keyName,
    align = "left",
  }: {
    label: string;
    keyName: SortKey;
    align?: "left" | "right";
  }) => (
    <th
      onClick={() => toggleSort(keyName)}
      style={{
        cursor: "pointer",
        userSelect: "none",
        textAlign: align,
      }}
    >
      {label} {sortKey === keyName ? (sortDir === "desc" ? "▼" : "▲") : ""}
    </th>
  );

  return (
    <section style={{ marginTop: "2rem" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <h2 className="card-title" style={{ marginBottom: "0.5rem" }}>
          Niche Gap Finder
        </h2>
        <p className="card-desc" style={{ margin: 0 }}>
          Find underserved SaaS niches. Set filters → see results.
        </p>
      </div>

      {/* Filters Card */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <p className="section-label" style={{ marginBottom: "1rem" }}>
          Filters
        </p>
        <div className="gap-finder-filters">
          <div className="filter-group">
            <label className="filter-label">Min Demand</label>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.7rem",
                color: "var(--text-muted)",
                marginBottom: 4,
              }}
            >
              <span>0</span>
              <span>{formatCompactNumber(maxDemand)}</span>
            </div>
            <div
              style={{
                "--slider-fill": `${maxDemand > 0 ? (minDemand / maxDemand) * 100 : 0}%`,
              } as React.CSSProperties}
            >
              <input
                type="range"
                className="metrics-slider"
                min={0}
                max={maxDemand}
                step={Math.max(1, Math.floor(maxDemand / 100))}
                value={minDemand}
                onChange={(e) => setMinDemand(Number(e.target.value))}
              />
            </div>
            <span style={{ fontSize: "0.85rem", fontWeight: 600, marginTop: 4 }}>
              {formatCompactNumber(minDemand)}
            </span>
          </div>
          <div className="filter-group">
            <label className="filter-label">Max Startups</label>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.7rem",
                color: "var(--text-muted)",
                marginBottom: 4,
              }}
            >
              <span>0</span>
              <span>{maxStartupsInData}</span>
            </div>
            <div
              style={{
                "--slider-fill": `${maxStartupsInData > 0 ? (maxStartups / maxStartupsInData) * 100 : 0}%`,
              } as React.CSSProperties}
            >
              <input
                type="range"
                className="metrics-slider"
                min={0}
                max={maxStartupsInData}
                step={1}
                value={maxStartups}
                onChange={(e) => setMaxStartups(Number(e.target.value))}
              />
            </div>
            <span style={{ fontSize: "0.85rem", fontWeight: 600, marginTop: 4 }}>
              {maxStartups}
            </span>
          </div>
          <div className="filter-group">
            <label className="filter-label">Min Rev / Visitor</label>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.7rem",
                color: "var(--text-muted)",
                marginBottom: 4,
              }}
            >
              <span>$0</span>
              <span>${maxRevPerVisitor.toFixed(2)}</span>
            </div>
            <div
              style={{
                "--slider-fill": `${maxRevPerVisitor > 0 ? (Math.min(minRevPerVisitor, maxRevPerVisitor) / maxRevPerVisitor) * 100 : 0}%`,
              } as React.CSSProperties}
            >
              <input
                type="range"
                className="metrics-slider"
                min={0}
                max={maxRevPerVisitor}
                step={Math.max(0.01, maxRevPerVisitor / 100)}
                value={Math.min(minRevPerVisitor, maxRevPerVisitor)}
                onChange={(e) => setMinRevPerVisitor(Number(e.target.value))}
              />
            </div>
            <span style={{ fontSize: "0.85rem", fontWeight: 600, marginTop: 4 }}>
              ${minRevPerVisitor.toFixed(2)}
            </span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "1rem",
            marginTop: "1.25rem",
            paddingTop: "1rem",
            borderTop: "1px solid var(--border)",
          }}
        >
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <span className="filter-label" style={{ marginRight: 4 }}>
              Quick filters
            </span>
            <button
              type="button"
              onClick={presets.goldmine}
              className="btn btn--sm btn--ghost"
            >
              Goldmine
            </button>
            <button
              type="button"
              onClick={presets.lowCompetition}
              className="btn btn--sm btn--ghost"
            >
              Low Competition
            </button>
            <button
              type="button"
              onClick={presets.highMonetization}
              className="btn btn--sm btn--ghost"
            >
              High Monetization
            </button>
            <button
              type="button"
              onClick={presets.highDemand}
              className="btn btn--sm btn--ghost"
            >
              High Demand
            </button>
          </div>
          {hasFiltersChanged && (
            <button
              type="button"
              onClick={resetFilters}
              style={{
                fontSize: "0.8rem",
                color: "var(--text-muted)",
                background: "none",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Reset filters
            </button>
          )}
        </div>

        <p
          style={{
            marginTop: "1rem",
            marginBottom: 0,
            fontSize: "0.9rem",
            color: "var(--text-secondary)",
          }}
        >
          {filtered.length} niche{filtered.length !== 1 ? "s" : ""} match
          {filtered.length === 1 ? "es" : ""} your filters
          {topNiche && filtered.length > 0 && (
            <span style={{ color: "var(--text-muted)" }}>
              {" "}
              — Top: {topNiche.label} (Gap Score {topNiche.gapScore != null ? Math.round(topNiche.gapScore).toLocaleString("en-US") : "—"})
            </span>
          )}
        </p>
      </div>

      {/* Results Card */}
      <div className="card">
        <p className="section-label" style={{ marginBottom: "1rem" }}>
          Results
        </p>

        {filtered.length === 0 ? (
          <div
            style={{
              padding: "2.5rem 1.5rem",
              textAlign: "center",
              background: "var(--bg-elevated)",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border)",
            }}
          >
            <p style={{ margin: "0 0 0.5rem", fontWeight: 600, color: "var(--text-primary)" }}>
              No niches match your filters.
            </p>
            <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
              Try:
            </p>
            <ul
              style={{
                margin: "0.5rem 0 0",
                paddingLeft: "1.25rem",
                fontSize: "0.9rem",
                color: "var(--text-muted)",
                textAlign: "left",
                display: "inline-block",
              }}
            >
              <li>Lowering minimum demand</li>
              <li>Increasing maximum startups</li>
              <li>Lowering revenue per visitor</li>
            </ul>
          </div>
        ) : (
          <div className="niche-table-wrap">
            <table className="niche-table">
              <thead>
                <tr>
                  <th>Niche</th>
                  <SortHeader label="Demand" keyName="marketDemand" align="right" />
                  <SortHeader label="Startups" keyName="startupCount" align="right" />
                  <SortHeader label="Rev/V" keyName="avgRevenuePerVisitor" align="right" />
                  <SortHeader label="Gap Score" keyName="gapScore" align="right" />
                  <SortHeader label="Opp" keyName="opportunityScore" align="right" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((niche) => {
                  const gapVariant = getGapScoreVariant(niche.gapScore);
                  const gapClass =
                    gapVariant === "high"
                      ? "cell-badge--success"
                      : gapVariant === "mid"
                        ? "cell-badge--warning"
                        : gapVariant === "low"
                          ? "cell-badge--low"
                          : "";
                  return (
                    <tr
                      key={niche.category}
                      onClick={() => router.push(`/dashboard/${niche.category}`)}
                      className="niche-row"
                      style={{ cursor: "pointer" }}
                    >
                      <td className="cell-name" style={{ fontWeight: 600 }}>
                        {niche.label}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {formatCompactNumber(niche.marketDemand ?? 0)}
                      </td>
                      <td style={{ textAlign: "right" }}>{niche.startupCount}</td>
                      <td style={{ textAlign: "right" }}>
                        {niche.avgRevenuePerVisitor != null &&
                        Number.isFinite(niche.avgRevenuePerVisitor)
                          ? `$${niche.avgRevenuePerVisitor.toFixed(2)}`
                          : "—"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {niche.gapScore != null ? (
                          <span
                            className={`cell-badge ${gapClass}`}
                            style={{
                              padding: "4px 10px",
                              borderRadius: "var(--radius-sm)",
                              fontWeight: 700,
                              fontSize: "0.85rem",
                            }}
                          >
                            {Math.round(niche.gapScore).toLocaleString("en-US")}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>
                        {niche.opportunityScore}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
